import { IconBaseProps } from "react-icons/lib";
import { FaUser, FaClipboardCheck } from "react-icons/fa6";
import { Box } from "@mui/material";
import { roles } from "..";
import { tokens } from "@/tokens";
import { PAGE_KEY_MAPPINGS } from "@/roles/protection/pageAccess";

import CoinsMask from "../../assets/svgs/coins_dollar.svg";
import ProductHubSvg from "../../assets/images/sidebar/new-icons/product-hub.svg";
import ChatAdvisorSvg from "../../assets/images/sidebar/new-icons/chat-advisor.svg";

import {
  AnalyticsIcon,
  PostImportIcon,
  DutyCalculatorIcon,
  SettingsIcon,
  PartnersIcon as PartnerIcon,
  ClientsIcon as ClientIcon,
} from "../../components/sidebar-icons";

// NEW NAVIGATION STRUCTURE:
// 1. Analytics
// 2. Customs ERP (NEW - consolidates Product Hub, Documents, Entries)
// 3. Riffs Intelligence
// 4. Compliance (NEW - Tariff Analysis, Post Import)
// 5. Tools (NEW - Duty Calculator, Unit Cost, Commerce Data, Scenario Modeling)
// 6. Potential Savings
// 7. Settings

const HOME_COMMON = [
  {
    href: `/dashboard/analytics`,
    icon: <AnalyticsIcon color={tokens.brand.accent} />,
    icon2: <AnalyticsIcon color={tokens.text.muted} />,
    title: "ACE Analytics",
  },
  {
    href: `/customs-erp/dashboard`,
    icon: (
      <Box sx={{ width: 19, height: 19, bgcolor: tokens.brand.accent, WebkitMask: `url(${ProductHubSvg}) no-repeat center / contain`, mask: `url(${ProductHubSvg}) no-repeat center / contain` }} />
    ),
    icon2: (
      <Box sx={{ width: 19, height: 19, bgcolor: tokens.text.muted, WebkitMask: `url(${ProductHubSvg}) no-repeat center / contain`, mask: `url(${ProductHubSvg}) no-repeat center / contain` }} />
    ),
    title: "Customs ERP",
  },
  {
    href: `/chat-advisor`,
    icon: (
      <Box sx={{ width: 19, height: 19, bgcolor: tokens.brand.accent, WebkitMask: `url(${ChatAdvisorSvg}) no-repeat center / contain`, mask: `url(${ChatAdvisorSvg}) no-repeat center / contain` }} />
    ),
    icon2: (
      <Box sx={{ width: 19, height: 19, bgcolor: tokens.text.muted, WebkitMask: `url(${ChatAdvisorSvg}) no-repeat center / contain`, mask: `url(${ChatAdvisorSvg}) no-repeat center / contain` }} />
    ),
    title: "Riffs Intelligence",
  },
  {
    href: `/compliance`,
    icon: <PostImportIcon color={tokens.brand.accent} />,
    icon2: <PostImportIcon color={tokens.text.muted} />,
    title: "Compliance",
  },
  {
    href: `/tools`,
    icon: <DutyCalculatorIcon color={tokens.brand.accent} />,
    icon2: <DutyCalculatorIcon color={tokens.text.muted} />,
    title: "Tools",
  },
  {
    href: `/first-sale`,
    icon: (
      <Box sx={{ width: 19, height: 19, bgcolor: tokens.brand.accent, WebkitMask: `url(${CoinsMask}) no-repeat center / contain`, mask: `url(${CoinsMask}) no-repeat center / contain` }} />
    ),
    icon2: (
      <Box sx={{ width: 19, height: 19, bgcolor: tokens.text.muted, WebkitMask: `url(${CoinsMask}) no-repeat center / contain`, mask: `url(${CoinsMask}) no-repeat center / contain` }} />
    ),
    title: "First Sale",
  },
  {
    href: `/dashboard/potential`,
    icon: <PostImportIcon color={tokens.brand.accent} />,
    icon2: <PostImportIcon color={tokens.text.muted} />,
    title: "Potential Savings",
  },
];

const DUTY_COLUMNS_ITEM = {
  href: `/duty-columns`,
  icon: <SettingsIcon color={tokens.brand.accent} />,
  icon2: <SettingsIcon color={tokens.text.muted} />,
  title: "Duty Columns",
};

const SETTINGS_ITEM = {
  href: `/settings/account-settings`,
  icon: <SettingsIcon color={tokens.brand.accent} />,
  icon2: <SettingsIcon color={tokens.text.muted} />,
  title: "Settings",
};

const OTHERS_COMMON = [SETTINGS_ITEM];

const SPOWNERItems = {
  home: [...HOME_COMMON],
  others: [
    {
      href: `/partners`,
      icon: <PartnerIcon color={tokens.brand.accent} />,
      icon2: <PartnerIcon color={tokens.text.muted} />,
      title: "Partners",
    },
    {
      href: `/clients`,
      icon: <ClientIcon color={tokens.brand.accent} />,
      icon2: <ClientIcon color={tokens.text.muted} />,
      title: "Clients",
    },
    ...OTHERS_COMMON,
  ],
};

const SPPARTNERItems = {
  home: [...HOME_COMMON],
  others: [...OTHERS_COMMON],
};

const SPCLIENTItems = {
  home: [...HOME_COMMON],
  others: [...OTHERS_COMMON],
};

const TDCLIENTItems = {
  home: [
    ...HOME_COMMON,
    {
      href: `/users`,
      icon: <ClientIcon color={tokens.brand.accent} />,
      icon2: <ClientIcon color={tokens.text.muted} />,
      title: "Users",
    },
  ],
  others: [...OTHERS_COMMON],
};

const TDADMINItems = {
  home: [...HOME_COMMON],
  others: [
    {
      href: `/tenant-management`,
      icon: <ClientIcon color={tokens.brand.accent} />,
      icon2: <ClientIcon color={tokens.text.muted} />,
      title: "Tenant Management",
    },
    ...OTHERS_COMMON,
  ],
};

export const SidebarItems = {
  SPOWNER: { ...SPOWNERItems },
  SPPARTNER: { ...SPPARTNERItems },
  SPCLIENT: { ...SPCLIENTItems },
  TDCLIENT: { ...TDCLIENTItems },
  TDADMIN: { ...TDADMINItems },
};

export const SETTINGS_MENU = [
  {
    title: "Account Settings",
    link: "account-settings",
    icon: (props: IconBaseProps) => <FaUser {...props} />,
    adminOnly: false,
  },
  {
    title: "Validation Checks",
    link: "validation-checks",
    icon: (props: IconBaseProps) => <FaClipboardCheck {...props} />,
    adminOnly: true,
  },
];

const filterHomeByPages = (pages: string[] | undefined) => {
  const hasAny = (keys: string[]) => !pages || keys.some((k) => pages.includes(k));
  return HOME_COMMON.filter((route) => {
    switch (route.title) {
      case 'ACE Analytics':
        return hasAny(['ANALYTICS']);
      case 'Customs ERP':
        return hasAny(PAGE_KEY_MAPPINGS['CUSTOMS_ERP']);
      case 'Riffs Intelligence':
        return hasAny(['CHAT_ASSISTANCE']);
      case 'Compliance':
        return hasAny([...PAGE_KEY_MAPPINGS['COMPLIANCE'], 'TRADE_DATA']);
      case 'Tools':
        return hasAny([...PAGE_KEY_MAPPINGS['TOOLS'], 'PRODUCT_HUB', 'TRADE_DATA']);
      case 'First Sale':
        return hasAny(['FIRST_SALE']);
      case 'Potential Savings':
        return hasAny(PAGE_KEY_MAPPINGS['POTENTIAL_SAVINGS']);
      default:
        return true;
    }
  });
};

export const getSpClientHomeItems = (user: IUser) => {
  return filterHomeByPages(user?.allowedPages);
};

export const getTDAdminOtherItems = (user: IUser, selectedTenant: ITenant | undefined) => {
  const isTDAdminSP =
    user?.userRole === roles.TDADMIN &&
    selectedTenant?.tenantType === "SERVICE_PROVIDER";

  // TD Admin always has access to Duty Columns
  let items = [
    {
      href: `/tenant-management`,
      icon: <ClientIcon color={tokens.brand.accent} />,
      icon2: <ClientIcon color={tokens.text.muted} />,
      title: "Tenant Management",
    },
    {
      href: `/refunds-analytics`,
      icon: <AnalyticsIcon color={tokens.brand.accent} />,
      icon2: <AnalyticsIcon color={tokens.text.muted} />,
      title: "Refunds Analytics",
    },
    DUTY_COLUMNS_ITEM,
    SETTINGS_ITEM,
  ];

  if (isTDAdminSP) {
    items = [
      {
        href: `/partners`,
        icon: <PartnerIcon color={tokens.brand.accent} />,
        icon2: <PartnerIcon color={tokens.text.muted} />,
        title: "Partners",
      },
      {
        href: `/clients`,
        icon: <ClientIcon color={tokens.brand.accent} />,
        icon2: <ClientIcon color={tokens.text.muted} />,
        title: "Clients",
      },
      ...items,
    ];
  }

  return items;
};

// SP Owner: get other items with optional Duty Columns based on permissions
export const getSpOwnerOtherItems = (user: IUser, tenantPages?: string[]) => {
  const allowedPages = user?.allowedPages ?? tenantPages;
  const hasDutyColumnsAccess = allowedPages ? allowedPages.includes('PRODUCT_HUB_DUTY_COLUMNS') : false;

  const items = [
    {
      href: `/partners`,
      icon: <PartnerIcon color={tokens.brand.accent} />,
      icon2: <PartnerIcon color={tokens.text.muted} />,
      title: "Partners",
    },
    {
      href: `/clients`,
      icon: <ClientIcon color={tokens.brand.accent} />,
      icon2: <ClientIcon color={tokens.text.muted} />,
      title: "Clients",
    },
  ];

  if (hasDutyColumnsAccess) {
    items.push(DUTY_COLUMNS_ITEM);
  }

  items.push(SETTINGS_ITEM);

  return items;
};

// TD Client: only show Users when current user is ADMIN
export const getTdClientHomeItems = (user: IUser, tenantPages?: string[]) => {
  const isAdmin = user?.accessLevel === 'ADMIN';
  const pages = user?.allowedPages ?? tenantPages ?? [];
  const items = filterHomeByPages(pages);
  if (isAdmin) {
    items.push({
      href: `/users`,
      icon: <ClientIcon color={tokens.brand.accent} />,
      icon2: <ClientIcon color={tokens.text.muted} />,
      title: "Users",
    });
  }
  return items;
};

// SP Owner: filter by allowedPages when present
export const getSpOwnerHomeItems = (user: IUser) => {
  return filterHomeByPages(user?.allowedPages);
};

// SP Partner: filter by allowedPages when present (same as SP Owner)
export const getSpPartnerHomeItems = (user: IUser) => {
  return getSpOwnerHomeItems(user);
};

// Generic filter using explicit allowed keys (no fallback — empty pages = nothing shown)
export const getHomeItemsByAllowedKeys = (allowedKeys: string[]) => {
  return filterHomeByPages(allowedKeys);
};
